import { parsePhoneNumberFromString } from "libphonenumber-js";
import { z } from "zod";

/**
 * Contact fields mirror the slots an Outlook contact offers, so nothing is lost on delivery:
 * 3 email addresses, 2 business phones, 1 mobile, 2 home phones, 3 postal addresses.
 */

export const PHONE_FIELDS = [
  "businessPhone1",
  "businessPhone2",
  "mobilePhone",
  "homePhone1",
  "homePhone2",
] as const;
export const EMAIL_FIELDS = ["email1", "email2", "email3"] as const;
export const ADDRESS_FIELDS = ["businessAddress", "homeAddress", "otherAddress"] as const;
export const TEXT_FIELDS = [
  "title",
  "givenName",
  "middleName",
  "surname",
  "nickName",
  "companyName",
  "department",
  "jobTitle",
  "profession",
  "officeLocation",
  "manager",
  "assistantName",
  "imAddress",
  "website",
  "notes",
] as const;

/**
 * Fields added after the first release (issue #30). Left out of the content hash while empty, so
 * adding them did not change the hash of every existing contact (no mass re-delivery).
 */
export const LATER_FIELDS = [
  "nickName",
  "profession",
  "officeLocation",
  "manager",
  "assistantName",
  "imAddress",
] as const;

export type PhoneField = (typeof PHONE_FIELDS)[number];
export type EmailField = (typeof EMAIL_FIELDS)[number];
export type AddressField = (typeof ADDRESS_FIELDS)[number];
export type TextField = (typeof TEXT_FIELDS)[number];

export const DEFAULT_PHONE_REGION = "CH";

/** Returns the phone number in E.164 format, or null when it is not a valid number. */
export function normalizePhone(raw: string, region = DEFAULT_PHONE_REGION): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const withPlus = trimmed.startsWith("00") ? `+${trimmed.slice(2)}` : trimmed;
  const parsed = parsePhoneNumberFromString(withPlus, region as never);
  if (!parsed?.isValid()) return null;
  return parsed.number;
}

/** Formats an E.164 number for display, e.g. +41 44 123 45 67. */
export function formatPhone(e164: string): string {
  const parsed = parsePhoneNumberFromString(e164);
  return parsed ? parsed.formatInternational() : e164;
}

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v ? v : null));

const phoneSchema = z
  .string()
  .optional()
  .transform((v, ctx) => {
    if (!v?.trim()) return null;
    const normalized = normalizePhone(v);
    if (!normalized) {
      ctx.addIssue({ code: "custom", message: "invalid_phone" });
      return z.NEVER;
    }
    return normalized;
  });

const emailSchema = z
  .string()
  .optional()
  .transform((v, ctx) => {
    if (!v?.trim()) return null;
    const value = v.trim().toLowerCase();
    if (!z.email().safeParse(value).success) {
      ctx.addIssue({ code: "custom", message: "invalid_email" });
      return z.NEVER;
    }
    return value;
  });

const websiteSchema = z
  .string()
  .optional()
  .transform((v, ctx) => {
    if (!v?.trim()) return null;
    let value = v.trim();
    if (!/^https?:\/\//i.test(value)) value = `https://${value}`;
    try {
      const url = new URL(value);
      if (!url.hostname.includes(".")) throw new Error("no tld");
      return value;
    } catch {
      ctx.addIssue({ code: "custom", message: "invalid_url" });
      return z.NEVER;
    }
  });

export const addressSchema = z
  .object({
    street: z.string().trim().max(200).optional().default(""),
    postalCode: z.string().trim().max(20).optional().default(""),
    city: z.string().trim().max(100).optional().default(""),
    state: z.string().trim().max(100).optional().default(""),
    country: z.string().trim().max(100).optional().default(""),
  })
  .nullish()
  .transform((a) => {
    if (!a) return null;
    const hasValue = Object.values(a).some((v) => v && v.length > 0);
    return hasValue ? a : null;
  });

export type Address = {
  street: string;
  postalCode: string;
  city: string;
  state: string;
  country: string;
};

export const contactInputSchema = z
  .object({
    title: optionalText(50),
    givenName: optionalText(100),
    middleName: optionalText(100),
    surname: optionalText(100),
    nickName: optionalText(100),
    companyName: optionalText(200),
    department: optionalText(200),
    jobTitle: optionalText(200),
    profession: optionalText(200),
    /** Site or office, e.g. "Zürich, 3. Stock". */
    officeLocation: optionalText(200),
    manager: optionalText(200),
    assistantName: optionalText(200),
    /** Teams or SIP address (Outlook "IM address"). */
    imAddress: optionalText(200),
    email1: emailSchema,
    email2: emailSchema,
    email3: emailSchema,
    businessPhone1: phoneSchema,
    businessPhone2: phoneSchema,
    mobilePhone: phoneSchema,
    homePhone1: phoneSchema,
    homePhone2: phoneSchema,
    businessAddress: addressSchema,
    homeAddress: addressSchema,
    otherAddress: addressSchema,
    website: websiteSchema,
    notes: optionalText(4000),
  })
  .superRefine((c, ctx) => {
    if (!c.givenName && !c.surname && !c.companyName) {
      ctx.addIssue({ code: "custom", path: ["surname"], message: "name_or_company_required" });
    }
  });

/** What a client sends. Values are raw strings, the schema normalizes them. */
export type ContactInputRaw = z.input<typeof contactInputSchema>;
/** Normalized contact data as stored and delivered. */
export type ContactData = z.output<typeof contactInputSchema>;

export function buildDisplayName(
  c: Pick<ContactData, "givenName" | "middleName" | "surname" | "companyName">,
) {
  const person = [c.givenName, c.middleName, c.surname].filter(Boolean).join(" ");
  return person || c.companyName || "";
}

export function emptyContactInput(): ContactInputRaw {
  return {
    title: "",
    givenName: "",
    middleName: "",
    surname: "",
    nickName: "",
    companyName: "",
    department: "",
    jobTitle: "",
    profession: "",
    officeLocation: "",
    manager: "",
    assistantName: "",
    imAddress: "",
    email1: "",
    email2: "",
    email3: "",
    businessPhone1: "",
    businessPhone2: "",
    mobilePhone: "",
    homePhone1: "",
    homePhone2: "",
    businessAddress: null,
    homeAddress: null,
    otherAddress: null,
    website: "",
    notes: "",
  };
}

/** Converts stored contact data back into form input (phones formatted for humans). */
export function contactToInput(c: ContactData): ContactInputRaw {
  const input = emptyContactInput() as Record<string, unknown>;
  for (const [key, value] of Object.entries(c)) {
    if (value === null || value === undefined) continue;
    input[key] = (PHONE_FIELDS as readonly string[]).includes(key) ? formatPhone(value as string) : value;
  }
  return input as ContactInputRaw;
}
